import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { notifications as seed } from "../data/notifications";
import type { AppNotification } from "../types/notifications";
interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  toggleRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => AppNotification | undefined;
  restore: (n: AppNotification) => void;
}
const NotificationsContext = createContext<NotificationsContextValue | null>(null);
export function NotificationsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<AppNotification[]>(seed);
  const markRead = useCallback((id: string) => {
    setItems(prev => prev.map(n => n.id === id && !n.read ? {
      ...n,
      read: true
    } : n));
  }, []);
  const toggleRead = useCallback((id: string) => {
    setItems(prev => prev.map(n => n.id === id ? {
      ...n,
      read: !n.read
    } : n));
  }, []);
  const markAllRead = useCallback(() => {
    setItems(prev => prev.map(n => n.read ? n : {
      ...n,
      read: true
    }));
  }, []);
  const dismiss = useCallback((id: string) => {
    const found = items.find(n => n.id === id);
    setItems(prev => prev.filter(n => n.id !== id));
    return found;
  }, [items]);
  const restore = useCallback((n: AppNotification) => {
    setItems(prev => {
      if (prev.some(x => x.id === n.id)) return prev;
      const rank = (id: string) => seed.findIndex(s => s.id === id);
      return [...prev, n].sort((a, b) => rank(a.id) - rank(b.id));
    });
  }, []);
  const value = useMemo(() => ({
    items,
    unreadCount: items.filter(n => !n.read).length,
    markRead,
    toggleRead,
    markAllRead,
    dismiss,
    restore
  }), [items, markRead, toggleRead, markAllRead, dismiss, restore]);
  return <NotificationsContext.Provider value={value} data-id="element-1248" data-mp="1d8ghaktujzn">{children}</NotificationsContext.Provider>;
}
export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}